/**
 * @namespace helpers
 */

/**
 * Helper functions for Objects
 *
 * @module object
 * @memberof helpers
 * @see helpers
 */

/**
 * Return any object property value
 *
 * @function
 * @param  {String} name
 * @param  {Object} o
 * @return {Object} object property value
 */
export const property = (name) => (o) =>
  o != null && typeof o === 'object'
    ? o[name]
    : undefined

/**
 * Return property value in lower case
 *
 * @function
 * @param  {String} name
 * @param  {Object} o
 * @return {String}
 */
export const lowerCaseProperty = (name) => (o) =>
  o != null && typeof o === 'object'
    ? String(o[name]).toLowerCase()
    : ''

/**
 * Returns whether or not an object has an own property with the specified name
 *
 * @function
 * @param  {String} name
 * @param  {Object} o
 * @return {Boolean}
 */
export const has = (name) => (o) =>
  Object.prototype.hasOwnProperty.call(o, name)

/**
 * Changes map to array of objects
 *
 * @function
 * @param  {String} keyName  - field name for key value
 * @param  {String} dataName - field name for data value
 * @param  {Object} o
 * @return {Array}
 *
 * @example
 * { "US": "United States",
 *   "AF": "Afghanistan",
 *   "AL": "Albania" }
 *
 *  -> toArray('code', 'name')(...) ->
 *
 *   [{ code: "US",
 *      name: "United States" },
 *    { code: "AF",
 *      name: "Afghanistan" },
 *    { code: "AL",
 *      name: "Albania" }]
 */
export const toArray = (keyName, dataName) => (o) => {
  const ret = []
  let counter = 0
  for (const k in o) {
    counter++
    if (has(k)(o)) {
      ret.push({
        id: counter,
        [keyName]: k,
        [dataName]: o[k]
      })
    }
  }
  return ret
}

/**
 * Returns partial copy of object containing only the keys specified
 * If key does not exist, property is ignored
 *
 * @function
 * @param  {String[]} names - properties' names
 * @param  {Object} o
 * @return {Object} partial copy of object
 */
export const pick = (...names) => (o) => {
  const ret = {}
  for (let i = 0, l = names.length; i < l; i++) {
    if (names[i] in o) {
      ret[names[i]] = o[names[i]]
    }
  }
  return ret
}

/**
 * Clone any object
 *
 * @function
 * @param {*} o
 * @return {*}
 */
export const clone = (o) => {
  if (typeof o !== 'object' || o === null) {
    return o
  }

  let ret

  if (Array.isArray(o)) {
    ret = []
    for (let i = 0, l = o.length; i < l; i++) {
      ret[i] = clone(o[i])
    }
    return ret
  }

  ret = {}
  for (const i in o) {
    if (has(i)(o)) {
      ret[i] = clone(o[i])
    }
  }
  return ret
}

/**
 * Sort function to use in Array.prototype.sort(...)
 *
 * @function
 * @param  {String} [direction] = asc (default) or desc
 * @param  {Function} prop - property getter
 * @return {Function}
 */
export const sortBy = (direction = 'asc') => (prop) =>
  (a, b) => {
    const pa = prop(a)
    const pb = prop(b)
    return (direction === 'asc' ? 1 : -1) * (pa > pb ? 1 : (pa < pb ? -1 : 0))
  }

/**
 * Ascending sort function to use in Array.prototype.sort(...)
 *
 * @function
 * @param  {Function} prop - property getter
 * @return {Function}
 */
export const ascBy = sortBy('asc')

/**
 * Descending sort function to use in Array.prototype.sort(...)
 *
 * @function
 * @param  {Function} prop - property getter
 * @return {Function}
 */
export const descBy = sortBy('desc')

/**
 * Returns the first element of the list which matches the predicate, or `undefined` if no element matches
 *
 * @param {Function} fn - predicate function used to determine if the element is the desired one
 * @param {Array} list - array to consider
 * @return {Object} element found, or `undefined`
 */
export const find = (fn) => (list) => {
  for (let i = 0, l = list.length; i < l; i++) {
    if (fn(list[i])) {
      return list[i]
    }
  }
}
